<template>
  <div class="quiz-results">
    <div class="results-header">
      <h2>Resultado do Quiz</h2>
    </div>

    <div class="results-summary">
      <div class="score-card" :class="results.passed ? 'passed' : 'failed'">
        <div class="score-number">{{ formattedPercentage }}%</div>
        <div class="score-label">
          {{ results.score }} de {{ results.total }} corretas
        </div>
        <div class="score-status" v-if="!isPreTest">
          <i :class="results.passed ? 'fa fa-check-circle' : 'fa fa-times-circle'"></i>
        </div>
        <div class="score-status" v-else>
          <i class="fa fa-info-circle"></i>
        </div>
      </div>

      <div class="results-message">
        <p v-if="results.passed && !isPreTest" class="success-message">
          Parabéns! Você atingiu a nota mínima necessária.
        </p>
        <p v-else-if="!results.passed && !isPreTest" class="error-message">
          Você não atingiu a nota mínima. Tente novamente!
        </p>
        <p v-else-if="isPreTest" class="info-message">
          Pré-teste concluído! Este teste serve para avaliar seu conhecimento atual.
        </p>
      </div>
    </div>

    <div class="questions-review">
      <h3>Revisão das Questões</h3>
      <div 
        v-for="(answer, index) in results.answers" 
        :key="answer.questionId"
        class="question-review-item"
        :class="answer.isCorrect ? 'correct' : 'incorrect'"
      >
        <div class="question-review-header">
          <span class="question-number">Questão {{ index + 1 }}</span>
          <span class="question-status">
            <i :class="answer.isCorrect ? 'fa fa-check-circle' : 'fa fa-times-circle'"></i>
            {{ answer.isCorrect ? 'Correto' : 'Incorreto' }}
          </span>
        </div>
        <div class="question-text">{{ answer.questionText }}</div>
        <div class="answer-details">
          <div class="your-answer">
            <strong>Sua resposta:</strong>
            <span :class="answer.isCorrect ? 'correct-text' : 'incorrect-text'">
              {{ getSelectedOptionsText(answer) }}
            </span>
          </div>
          <div v-if="!answer.isCorrect && !isPreTest" class="correct-answer">
            <strong>Resposta correta:</strong>
            <span class="correct-text">{{ getCorrectOptionsText(answer) }}</span>
          </div>
          <div v-if="answer.feedback && !isPreTest" class="question-feedback">
            <strong>Feedback:</strong>
            <p>{{ answer.feedback }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="results-actions">
      <button 
        v-if="canTryAgain"
        class="btn btn-secondary"
        @click="$emit('tryAgain')"
      >
        Tentar Novamente
      </button>
      <button 
        class="btn btn-primary"
        @click="$emit('nextLesson')"
      >
        {{ results.passed ? 'Próxima Lesson' : 'Voltar ao Curso' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  results: {
    type: Object,
    required: true
  },
  lesson: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['tryAgain', 'nextLesson']);

// Check if this is a pre-test
const isPreTest = computed(() => {
  return props.results.quizType === 'PRE_TEST';
});

// Format percentage without excessive decimals
const formattedPercentage = computed(() => {
  return Math.round(props.results.percentage);
});

const canTryAgain = computed(() => {
  // This would need to check attempts remaining from API
  return !props.results.passed && !isPreTest.value;
});

const getSelectedOptionsText = (answer) => {
  if (!answer.selectedOptionIds || answer.selectedOptionIds.length === 0) {
    return 'Não respondida';
  }
  
  if (!answer.options || answer.options.length === 0) {
    return 'N/A';
  }
  
  // Map option IDs to option texts
  const selectedOptions = answer.options.filter(opt => 
    answer.selectedOptionIds.includes(opt.id)
  );
  
  if (selectedOptions.length === 0) {
    return 'Não respondida';
  }
  
  return selectedOptions.map(o => o.text).join(', ');
};

const getCorrectOptionsText = (answer) => {
  if (!answer.correctOptionIds || answer.correctOptionIds.length === 0) {
    return 'N/A';
  }
  
  if (!answer.options || answer.options.length === 0) {
    return 'N/A';
  }
  
  // Map option IDs to option texts
  const correctOptions = answer.options.filter(opt => 
    answer.correctOptionIds.includes(opt.id)
  );
  
  if (correctOptions.length === 0) {
    return 'N/A';
  }
  
  return correctOptions.map(o => o.text).join(', ');
};
</script>

<style scoped>
.quiz-results {
  background: white;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.results-header {
  text-align: center;
  margin-bottom: 30px;
}

.results-summary {
  text-align: center;
  margin-bottom: 40px;
}

.score-card {
  display: inline-block;
  padding: 30px 50px;
  border-radius: 12px;
  margin-bottom: 20px;
}

.score-card.passed {
  background: #e8f5e9;
  border: 2px solid #129f46;
}

.score-card.failed {
  background: #ffebee;
  border: 2px solid #f44336;
}

.score-number {
  font-size: 48px;
  font-weight: bold;
  margin-bottom: 10px;
}

.score-card.passed .score-number {
  color: #129f46;
}

.score-card.failed .score-number {
  color: #f44336;
}

.score-label {
  font-size: 18px;
  color: #666;
  margin-bottom: 10px;
}

.score-status {
  font-size: 20px;
  font-weight: bold;
  margin-top: 10px;
}

.score-status i {
  font-size: 28px;
}

.score-card.passed .score-status {
  color: #129f46;
}

.score-card.failed .score-status {
  color: #f44336;
}

.results-message {
  margin-top: 20px;
}

.success-message {
  color: #129f46;
  font-size: 18px;
  font-weight: 500;
}

.error-message {
  color: #f44336;
  font-size: 18px;
  font-weight: 500;
}

.questions-review {
  margin-top: 40px;
}

.questions-review h3 {
  margin-bottom: 20px;
}

.question-review-item {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.question-review-item.correct {
  border-color: #129f46;
  background: #f1f8f4;
}

.question-review-item.incorrect {
  border-color: #f44336;
  background: #fff5f5;
}

.question-review-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.question-number {
  font-weight: bold;
  font-size: 16px;
}

.question-status {
  font-weight: bold;
}

.question-review-item.correct .question-status {
  color: #129f46;
}

.question-review-item.incorrect .question-status {
  color: #f44336;
}

.question-text {
  font-size: 16px;
  margin-bottom: 15px;
  font-weight: 500;
}

.answer-details {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #e9ecef;
}

.your-answer,
.correct-answer {
  margin-bottom: 10px;
}

.correct-text {
  color: #129f46;
  font-weight: 500;
}

.incorrect-text {
  color: #f44336;
  font-weight: 500;
}

.question-feedback {
  margin-top: 15px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 6px;
}

.results-actions {
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 40px;
}
</style>

